// [2026-02-07] style-extraction: moved inline styles to SCSS module
import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { BroadcastItemCreate } from "../../services/broadcastService";
import { Currency } from "../../interfaces/Currency";
import BroadcastItemRow from "./BroadcastItemRow";
import { makeBlankBroadcastItem } from "./broadcast-form/utils/broadcastHelpers";
import styles from "./Broadcast.module.scss";

interface BroadcastItemListProps {
  items: BroadcastItemCreate[];
  userCurrency: Currency;
  maxItems: number;
  onAddItem: () => void;
  arrayHelpers: {
    push: (obj: any) => void;
    remove: (index: number) => void;
    replace: (index: number, value: any) => void;
  };
  onItemChange: (index: number, field: string, value: any) => void;
  errors?: any;
  touched?: any;
}

const BroadcastItemList: React.FC<BroadcastItemListProps> = ({
  items,
  userCurrency,
  maxItems,
  onAddItem,
  arrayHelpers,
  onItemChange,
  errors,
  touched,
}) => {
  const { values, setTouched, setErrors, setFieldValue } = useFormikContext<any>();
  const prevBroadcastRef = useRef<any[]>([]);

  // Keep legacy items array in sync with broadcast array
  useEffect(() => {
    const currentBroadcast = values.broadcast || [];
    const prevBroadcast = prevBroadcastRef.current;

    // Only update if broadcast array actually changed (avoid infinite loops)
    if (JSON.stringify(currentBroadcast) !== JSON.stringify(prevBroadcast)) {
      prevBroadcastRef.current = currentBroadcast;
      setFieldValue("items", currentBroadcast, false);
    }
  }, [values.broadcast, setFieldValue]);

  // Delete handler: reset if single row, remove if multiple rows
  const onDeleteRow = (index: number) => {
    if (items.length === 1) {
      // Reset the single remaining row to defaults (like Clear Data button)
      const blank = makeBlankBroadcastItem(userCurrency);
      arrayHelpers.replace(0, blank);

      // Clear touched and errors for the reset row
      setTimeout(() => {
        setTouched({});
        setErrors({});
      }, 0);
    } else {
      // Remove the row (multiple rows exist)
      // Just use arrayHelpers.remove - useEffect will sync items array
      arrayHelpers.remove(index);
    }
  };

  const listDeleteTooltip = items.length === 1 ? "Reset this row to blank" : "Delete this row";

  return (
    <div className={`p-3 mb-3 ${styles.fullWidthContainer}`}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="m-0 fw-semibold">Broadcast Group</h5>
        <span className="p-small text-muted">
          Broadcasts ({items.length}/{maxItems})
        </span>
      </div>
      {items.map((item, index) => {
        const anyItem: any = item as any;
        if (anyItem.__pending) {
          return (
            <div key={index} className="mb-3">
              <div className="border rounded p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0 text-muted">Broadcast {index + 1}</h6>
                  <button
                    type="button"
                    className="btn-danger-figma btn-sm"
                    onClick={() => onDeleteRow(index)}
                    title={listDeleteTooltip}
                  >
                    Delete
                  </button>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></div>
                  <div>
                    <div className="fw-semibold">Analyzing photo…</div>
                    <div className="text-muted small">Looking for manufacturer & part number</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }

        if (anyItem.__error) {
          return (
            <div key={index} className="mb-3">
              <div className="border rounded p-3 bg-light border-danger">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0 text-danger">Analysis failed</h6>
                  <button
                    type="button"
                    className="btn-danger-figma btn-sm"
                    onClick={() => onDeleteRow(index)}
                    title={listDeleteTooltip}
                  >
                    Delete
                  </button>
                </div>
                <div className="text-danger small">
                  {anyItem.__errorMessage || "Could not analyze image"}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={index} className="mb-3">
            <BroadcastItemRow
              item={item}
              index={index}
              userCurrency={userCurrency}
              onItemChange={onItemChange}
              totalItems={items.length}
              onRemoveItem={onDeleteRow}
            />
          </div>
        );
      })}

      {items.length === 0 && (
        <div className="text-center py-4 text-muted">
          <p className="mb-2">No broadcasts added yet</p>
          <button type="button" className="btn-add-item" onClick={onAddItem}>
            <FontAwesomeIcon icon={faPlus} />
            Add First Broadcast
          </button>
        </div>
      )}

      {items.length > 0 && (
        <div className="d-flex justify-content-start mt-2">
          <button
            type="button"
            className="btn-add-item btn-sm"
            onClick={onAddItem}
            disabled={items.length >= maxItems}
            title={
              items.length >= maxItems ? `Maximum ${maxItems} broadcasts allowed` : "Add broadcast"
            }
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Broadcast
          </button>
        </div>
      )}
    </div>
  );
};

export default BroadcastItemList;
